/**
 * Error handling utilities for consistent error message extraction and retry logic
 */

export interface ErrorResponse {
  message: string;
  code?: string;
  details?: any;
}

/**
 * Extract user-friendly error message from API error response
 */
export function extractErrorMessage(err: any, defaultMessage: string = 'An error occurred'): string {
  // Check for structured error response
  if (err.response?.data?.error?.message) {
    return err.response.data.error.message;
  }
  
  // Check for FastAPI validation errors
  if (err.response?.data?.detail) {
    const detail = err.response.data.detail;
    
    if (typeof detail === 'string') {
      return detail;
    }
    
    if (Array.isArray(detail)) {
      return detail.map((e: any) => {
        if (typeof e === 'object' && e.msg) {
          const field = e.loc?.join('.') || 'Field';
          return `${field}: ${e.msg}`;
        }
        return String(e);
      }).join(', ');
    }
  }
  
  // Check for generic error message
  if (err.message) {
    return err.message;
  }
  
  // Check for network errors
  if (err.code === 'ECONNABORTED') {
    return 'Request timeout. Please try again.';
  }
  
  if (err.code === 'ERR_NETWORK') {
    return 'Network error. Please check your connection.';
  }
  
  // Check for HTTP status codes
  if (err.response?.status) {
    switch (err.response.status) {
      case 401:
        return 'Authentication required. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Request failed with status ${err.response.status}`;
    }
  }
  
  return defaultMessage;
}

/**
 * Check if an error is retryable (transient failure)
 */
export function isRetryableError(err: any): boolean {
  // Network errors are retryable
  if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK') {
    return true;
  }
  
  // Timeout errors are retryable
  if (err.message?.toLowerCase().includes('timeout')) {
    return true;
  }
  
  // 5xx server errors are retryable
  if (err.response?.status >= 500 && err.response?.status < 600) {
    return true;
  }
  
  // 429 rate limit errors are retryable
  if (err.response?.status === 429) {
    return true;
  }
  
  return false;
}

/**
 * Retry configuration
 */
export interface RetryConfig {
  maxAttempts?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
}

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxAttempts: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
};

/**
 * Sleep utility for delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Execute a function with retry logic for transient failures
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> {
  const {
    maxAttempts,
    initialDelay,
    maxDelay,
    backoffMultiplier,
  } = { ...DEFAULT_RETRY_CONFIG, ...config };
  
  let lastError: any;
  let delay = initialDelay;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      
      // Don't retry if it's not a retryable error
      if (!isRetryableError(err)) {
        throw err;
      }
      
      // Don't retry if we've exhausted attempts
      if (attempt >= maxAttempts) {
        throw err;
      }
      
      // Wait before retrying with exponential backoff
      await sleep(Math.min(delay, maxDelay));
      delay *= backoffMultiplier;
      
      console.log(`Retry attempt ${attempt}/${maxAttempts} after error:`, extractErrorMessage(err));
    }
  }
  
  throw lastError;
}

/**
 * Loading state manager for consistent loading indicators
 */
export class LoadingManager {
  private loadingStates: Map<string, boolean> = new Map();
  private listeners: Set<() => void> = new Set();
  
  setLoading(key: string, loading: boolean): void {
    this.loadingStates.set(key, loading);
    this.notifyListeners();
  }
  
  isLoading(key: string): boolean {
    return this.loadingStates.get(key) || false;
  }
  
  isAnyLoading(): boolean {
    return Array.from(this.loadingStates.values()).some(loading => loading);
  }
  
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }
}
